import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";

import PersonIcon from "@mui/icons-material/Person";
import { Avatar, Box, CssBaseline } from "@mui/material";
import MuiAppBar from "@mui/material/AppBar";
import Divider from "@mui/material/Divider";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/system";
import React, { useState, useEffect } from "react";
import ListItems from "./listItems";
import WhatsAppStatusIndicator from "./shared/WhatsAppStatusIndicator";
import NotificationIcon from "./notifications/NotificationIcon";

const drawerWidth = 250;
const drawerCollapsedWidth = 72;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "fixed",
    whiteSpace: "nowrap",
    width: drawerWidth,
    height: "100vh",
    overflowY: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: drawerCollapsedWidth,
    }),
  },
}));

export default function AppBarMenu({ children, icons = null }) {
  // Inicializar o estado do menu a partir do localStorage
  const [open, setOpen] = useState(() => {
    const savedState = localStorage.getItem('menuDrawerOpen');
    return savedState !== null ? JSON.parse(savedState) : true;
  });

  // Persistir o estado do menu no localStorage
  useEffect(() => {
    localStorage.setItem('menuDrawerOpen', JSON.stringify(open));
  }, [open]);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const theme = useTheme();
  const textColor = theme.palette.text.primary;
  const dividerColor = theme.palette.icon.secondary;

  const { userName } = JSON.parse(localStorage.getItem("user"));

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <AppBar
        position="fixed"
        open={open}
        sx={{ backgroundColor: theme.palette.primary.main }}
      >
        <Toolbar sx={{ position: "relative" }}> {/* // 1. Adicionado para ser a referência do posicionamento absoluto */}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{
              marginRight: "36px",
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* // 2. Container para centralização absoluta do botão de relatório */}
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            {icons ? icons : ""}
          </Box>
          
          {/* // 3. Este container agora funciona como esperado, pois o item central está fora do fluxo */}
          <Box sx={{ marginLeft: "auto", marginRight: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <WhatsAppStatusIndicator />
            <NotificationIcon />
          </Box>
          
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        open={open}
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.default,
            color: textColor,
          },
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: open ? "space-around" : "center",
            px: open ? [0] : [1],
            minHeight: "64px !important",
          }}
        >
          <Avatar sx={{ display: open ? 'flex' : 'flex' }}>
            <PersonIcon />
          </Avatar>
          {open && (
            <>
              <Typography
                sx={{
                  fontWeight: "bold",
                  maxWidth: "110px",
                  whiteSpace: "pre-line",
                }}
              >
                {userName}
              </Typography>

              <IconButton onClick={toggleDrawer}>
                <ChevronLeftIcon style={{ color: textColor }} />
              </IconButton>
            </>
          )}
          {!open && (
            <IconButton 
              onClick={toggleDrawer}
              sx={{
                position: 'absolute',
                right: -12,
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${dividerColor}`,
                width: 24,
                height: 24,
                '&:hover': {
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            >
              <ChevronRightIcon style={{ color: textColor, fontSize: 16 }} />
            </IconButton>
          )}
        </Toolbar>
        <Divider sx={{ backgroundColor: dividerColor }} />
        <List component="nav" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', py: 0 }}>
          <ListItems open={open} />
        </List>
      </Drawer>

      <Box
        component="main"
        sx={{
          backgroundColor: theme.palette.background.secondary,
          flexGrow: 1,
          minHeight: "100vh",
          mt: "64px", // Altura da AppBar para não sobrepor conteúdo
          ml: open ? `${drawerWidth}px` : `${drawerCollapsedWidth}px`, // Margem do drawer
          transition: theme.transitions.create(['margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
}